import React from "react";
import { CheckCircle, Database, Zap, AlertCircle, Plus, Puzzle } from "lucide-react";
import PageHeader, { PrimaryButton, PageKPIItem } from "components/ui/PageHeader";

interface IntegrationsHeaderProps {
    activeIntegrations?: number | null;
    totalSynced?: number | null;
    availableIntegrations?: number | null;
    syncErrors?: number | null;
    onAddIntegration: () => void;
    isLoading?: boolean;
}

const IntegrationsHeader: React.FC<IntegrationsHeaderProps> = ({
    activeIntegrations = 0,
    totalSynced = null,
    availableIntegrations = 0,
    syncErrors = null,
    onAddIntegration,
    isLoading = false,
}) => {
    /* A tile with no number is not a tile. `totalSynced` and `syncErrors` have
       no field behind them in the integrations payload, so rather than render
       "—" forever, the strip drops them and shows only what it can measure. */
    const kpis: PageKPIItem[] = ([
        {
            icon: CheckCircle,
            value: activeIntegrations,
            label: "Active Integrations",
            accentColor: "bg-gradient-to-r from-emerald-400 to-teal-500",
            accentHex: "#10b981",
            iconBg: "bg-emerald-50 dark:bg-emerald-900/20",
            iconColor: "text-emerald-600 dark:text-emerald-400",
        },
        {
            icon: Database,
            value: totalSynced,
            label: "Records Synced",
            accentColor: "bg-gradient-to-r from-blue-500 to-indigo-500",
            accentHex: "#6366f1",
            iconBg: "bg-blue-50 dark:bg-blue-900/20",
            iconColor: "text-blue-600 dark:text-blue-400",
        },
        {
            icon: Zap,
            value: availableIntegrations,
            label: "Available",
            accentColor: "bg-gradient-to-r from-violet-500 to-purple-500",
            accentHex: "#8b5cf6",
            iconBg: "bg-violet-50 dark:bg-violet-900/20",
            iconColor: "text-violet-600 dark:text-violet-400",
        },
        {
            icon: AlertCircle,
            value: syncErrors,
            label: "Sync Errors",
            accentColor: "bg-gradient-to-r from-red-400 to-rose-500",
            accentHex: "#f43f5e",
            iconBg: "bg-red-50 dark:bg-red-900/20",
            iconColor: "text-red-500 dark:text-red-400",
        },
    ] as Array<PageKPIItem & { value: number | string | null }>).filter(
        (k) => k.value !== null && k.value !== undefined,
    );

    return (
        <PageHeader
            icon={<Puzzle className="w-5 h-5" />}
            iconBg="bg-blue-50 dark:bg-blue-900/20"
            iconColor="text-blue-600 dark:text-blue-400"
            accentColor="bg-gradient-to-r from-blue-500 to-indigo-500"
            title="Integrations"
            subtitle="Connect your platform with external services"
            actions={
                <PrimaryButton icon={<Plus className="w-4 h-4" />} onClick={onAddIntegration}>
                    Add Integration
                </PrimaryButton>
            }
            kpis={kpis}
            isLoading={isLoading}
        />
    );
};

export default IntegrationsHeader;
